import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { estados } from '../utils/estados';
import { countries } from '../utils/countries';
import './RegisterForm.css';

type ProfessionType = 'MEDICO' | 'ESTUDANTE' | 'NAO_MEDICO';

interface RegisterFormData {
  nome: string;
  sobrenome: string;
  email: string;
  whatsapp: string;
  pais: string;
  estado: string;
  profissao: ProfessionType;
  crm?: string;
  especialidade?: string;
  faculdade?: string;
  anoFormacao?: number;
  senha: string;
  confirmarSenha: string;
}

interface Estado {
  sigla: string;
  nome: string;
}

const formatWhatsApp = (value: string) => {
  // Remove todos os caracteres não numéricos
  const numbers = value.replace(/\D/g, '');
  
  // Aplica a máscara conforme o usuário digita
  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 7) {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  } else {
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  }
};

export const RegisterForm: React.FC = () => {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const { register, handleSubmit, watch, formState: { errors }, setValue } = useForm<RegisterFormData>({
    defaultValues: {
      profissao: undefined // Força o usuário a selecionar uma opção
    }
  });
  const [pais, setPais] = useState('Brasil');
  const [profissao, setProfissao] = useState<ProfessionType | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setError(null);
      const registerData = {
        name: data.nome,
        sobrenome: data.sobrenome,
        email: data.email,
        whatsapp: data.whatsapp,
        pais: data.pais,
        estado: data.estado,
        profissao: data.profissao,
        crm: data.crm,
        especialidade: data.especialidade,
        faculdade: data.faculdade,
        anoFormacao: data.anoFormacao ? Number(data.anoFormacao) : undefined,
        password: data.senha,
      };
      console.log('📤 Dados a serem enviados:', registerData);
      await registerUser(registerData);
      navigate('/');
    } catch (error: any) {
      console.error('❌ Erro detalhado no RegisterForm:', {
        error,
        name: error?.name,
        message: error?.message,
        response: error?.response,
        responseData: error?.response?.data,
        stack: error?.stack
      });

      // Tenta obter a mensagem de erro da forma mais detalhada possível
      const errorMessage = 
        error?.response?.data?.message || // Mensagem da API
        error?.response?.data || // Resposta direta da API
        error?.message || // Mensagem do erro
        'Erro desconhecido'; // Fallback

      console.log('📝 Mensagem final:', errorMessage);

      if (typeof errorMessage === 'string' && 
          (errorMessage.includes('Email já cadastrado') || 
           errorMessage.includes('já existe') || 
           errorMessage.includes('duplicate'))) {
        setError('Este email já está cadastrado. Por favor, use outro email ou faça login.');
      } else {
        setError(`Erro ao criar conta: ${errorMessage}`);
      }
    }
  };

  return (
    <div className="register-form-container">
      <div className="register-form-header">
        <img src="/assets/images/logo.png" alt="Logo Biblioteca MD Ensino" width="96" height="96" />
        <h1>Biblioteca MD Ensino</h1>
      </div>
      <h2>Cadastro de Usuário</h2>
      {error && <div className="error">{error}</div>}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-group">
          <label>Nome</label>
          <input
            type="text"
            {...register('nome', { required: 'Nome é obrigatório' })}
          />
          {errors.nome && <span className="error">{errors.nome.message}</span>}
        </div>

        <div className="form-group">
          <label>Sobrenome</label>
          <input
            type="text"
            {...register('sobrenome', { required: 'Sobrenome é obrigatório' })}
          />
          {errors.sobrenome && <span className="error">{errors.sobrenome.message}</span>}
        </div>

        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            {...register('email', {
              required: 'Email é obrigatório',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Email inválido'
              }
            })}
          />
          {errors.email && <span className="error">{errors.email.message}</span>}
        </div>

        <div className="form-group spaced">
          <label>WhatsApp (com DDD)</label>
          <input
            type="tel"
            placeholder="(11) 99999-9999"
            {...register('whatsapp', {
              required: 'WhatsApp é obrigatório',
              pattern: {
                value: /^\([1-9]{2}\) (?:[2-8]|9[1-9])[0-9]{3}-[0-9]{4}$/,
                message: 'Formato inválido. Use (99) 99999-9999'
              }
            })}
            onChange={(e) => {
              const formatted = formatWhatsApp(e.target.value);
              e.target.value = formatted;
              setValue('whatsapp', formatted);
            }}
            maxLength={15}
          />
          {errors.whatsapp && <span className="error">{errors.whatsapp.message}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="pais">País</label>
          <select
            id="pais"
            name="pais"
            value={pais}
            onChange={(e) => {
              setPais(e.target.value);
            }}
            required
          >
            <option value="">Selecione um país</option>
            {countries.map(country => (
              <option key={country.code} value={country.name}>
                {country.name}
              </option>
            ))}
          </select>
        </div>

        {pais === 'Brasil' && (
          <div className="form-group">
            <label>Estado</label>
            <select {...register('estado', { required: 'Estado é obrigatório' })}>
              {estados.map((estado: Estado) => (
                <option key={estado.sigla} value={estado.sigla}>
                  {estado.nome}
                </option>
              ))}
            </select>
            {errors.estado && <span className="error">{errors.estado.message}</span>}
          </div>
        )}

        <div className="form-group">
          <label>Formação</label>
          <select
            {...register('profissao', { required: 'Formação é obrigatória' })}
            onChange={(e) => setProfissao(e.target.value as ProfessionType)}
            value={profissao}
          >
            <option value="">Selecione sua formação</option>
            <option value="MEDICO">Médico</option>
            <option value="ESTUDANTE">Estudante de Medicina</option>
            <option value="NAO_MEDICO">Não médico</option>
          </select>
          {errors.profissao && <span className="error">{errors.profissao.message}</span>}
        </div>

        {profissao === 'MEDICO' && (
          <>
            <div className="form-group">
              <label>CRM</label>
              <input
                type="text"
                {...register('crm', { required: 'CRM é obrigatório' })}
              />
              {errors.crm && <span className="error">{errors.crm.message}</span>}
            </div>

            <div className="form-group">
              <label>Especialidade</label>
              <input
                type="text"
                {...register('especialidade')}
                placeholder="Sem especialidade"
              />
            </div>
          </>
        )}

        {profissao === 'ESTUDANTE' && (
          <>
            <div className="form-group">
              <label>Faculdade</label>
              <input
                type="text"
                {...register('faculdade', { required: 'Faculdade é obrigatória' })}
              />
              {errors.faculdade && <span className="error">{errors.faculdade.message}</span>}
            </div>

            <div className="form-group">
              <label>Ano de Conclusão</label>
              <input
                type="number"
                {...register('anoFormacao', {
                  required: 'Ano de conclusão é obrigatório',
                  min: {
                    value: new Date().getFullYear(),
                    message: 'O ano deve ser maior ou igual ao atual'
                  }
                })}
              />
              {errors.anoFormacao && <span className="error">{errors.anoFormacao.message}</span>}
            </div>
          </>
        )}

        <div className="form-group">
          <label>Senha</label>
          <input
            type="password"
            {...register('senha', {
              required: 'Senha é obrigatória',
              minLength: {
                value: 6,
                message: 'A senha deve ter no mínimo 6 caracteres'
              }
            })}
          />
          {errors.senha && <span className="error">{errors.senha.message}</span>}
        </div>

        <div className="form-group">
          <label>Confirmar Senha</label>
          <input
            type="password"
            {...register('confirmarSenha', {
              required: 'Confirmação de senha é obrigatória',
              validate: (value) =>
                value === watch('senha') || 'As senhas não conferem'
            })}
          />
          {errors.confirmarSenha && <span className="error">{errors.confirmarSenha.message}</span>}
        </div>

        <button type="submit" className="submit-button">
          Cadastrar
        </button>

        <div className="links">
          <a href="/login">
            Voltar para o login
          </a>
        </div>
      </form>
    </div>
  );
}; 